import React, { useState, useEffect } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  MapPin,
  Briefcase,
  Building2,
  ExternalLink,
  MessageSquare,
  CheckCircle2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { getDisplayName } from '@shared/utils/display-name';

interface ProfileData {
  id: string;
  name: string;
  avatar?: string;
  role: 'client' | 'filmmaker';
  verified?: boolean;
  // Filmmaker fields
  location?: string;
  serviceTypes?: string[];
  bio?: string;
  // Client fields
  companyName?: string;
  industry?: string;
  projectCount?: number;
}

interface ProfilePreviewPopoverProps {
  entityId: string;
  entityRole: 'client' | 'filmmaker';
  children: React.ReactNode;
  onViewFullProfile?: () => void;
  onStartChat?: () => void;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
}

export function ProfilePreviewPopover({
  entityId,
  entityRole,
  children,
  onViewFullProfile,
  onStartChat,
  side = 'right',
  align = 'start',
}: ProfilePreviewPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);

  useEffect(() => {
    if (isOpen && entityId && entityRole) {
      fetchProfile();
    }
  }, [isOpen, entityId, entityRole]);

  const fetchProfile = async () => {
    setIsLoading(true);
    try {
      if (entityRole === 'filmmaker') {
        const { data, error } = await supabase
          .from('filmmaker')
          .select(`
            filmmaker_id,
            user_id,
            first_name,
            last_name,
            profile_pic,
            verified,
            bio,
            filmmakers_services (
              service_types (
                service_name
              )
            ),
            filmmaker_locations (
              location_name
            )
          `)
          .eq('filmmaker_id', entityId)
          .single();

        if (error) throw error;

        // Fetch privacy settings from users table
        let privacyData: { username: string | null; show_username_only: boolean } | null = null;
        if (data.user_id) {
          const { data: userData } = await supabase
            .from('users')
            .select('username, show_username_only')
            .eq('user_id', data.user_id)
            .single();
          if (userData) {
            privacyData = {
              username: userData.username,
              show_username_only: userData.show_username_only || false
            };
          }
        }

        let avatarUrl = data.profile_pic;
        if (avatarUrl && !avatarUrl.startsWith('http')) {
          const { data: urlData } = supabase.storage.from('public-assets').getPublicUrl(avatarUrl);
          avatarUrl = urlData.publicUrl;
        }

        // Get location from filmmaker_locations
        const locationName = data.filmmaker_locations?.[0]?.location_name || '';

        // Use shared utility for display name (respects privacy setting)
        const displayName = getDisplayName({
          first_name: data.first_name,
          last_name: data.last_name,
          username: privacyData?.username,
          show_username_only: privacyData?.show_username_only,
        }, 'Filmmaker');

        setProfile({
          id: data.filmmaker_id,
          name: displayName,
          avatar: avatarUrl,
          role: 'filmmaker',
          verified: data.verified,
          location: locationName,
          serviceTypes: data.filmmakers_services?.map((fs: any) => fs.service_types?.service_name).filter(Boolean) || [],
          bio: data.bio,
        });
      } else {
        const { data, error } = await supabase
          .from('client')
          .select(`
            client_id,
            company_name,
            logo_url,
            verified,
            industry,
            company_description
          `)
          .eq('client_id', entityId)
          .single();

        if (error) throw error;

        let avatarUrl = data.logo_url;
        if (avatarUrl && !avatarUrl.startsWith('http')) {
          const { data: urlData } = supabase.storage.from('public-assets').getPublicUrl(avatarUrl);
          avatarUrl = urlData.publicUrl;
        }

        // Get project count
        const { count } = await supabase
          .from('client_projects')
          .select('*', { count: 'exact', head: true })
          .eq('client_id', entityId);

        setProfile({
          id: data.client_id,
          name: data.company_name || 'Client',
          avatar: avatarUrl,
          role: 'client',
          verified: data.verified,
          companyName: data.company_name,
          industry: data.industry,
          projectCount: count || 0,
          bio: data.company_description,
        });
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        className="w-72 p-0"
        sideOffset={8}
      >
        {isLoading ? (
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        ) : profile ? (
          <div>
            {/* Header */}
            <div className="p-4 border-b bg-muted/30">
              <div className="flex items-start gap-3">
                <Avatar className="h-12 w-12 ring-2 ring-background">
                  {profile.avatar ? (
                    <AvatarImage src={profile.avatar} alt={profile.name} />
                  ) : (
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {profile.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h4 className="font-semibold text-sm truncate">{profile.name}</h4>
                    {profile.verified && (
                      <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground capitalize">
                    {profile.role}
                  </p>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="p-4 space-y-3">
              {/* Location */}
              {profile.location && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="truncate">{profile.location}</span>
                </div>
              )}

              {/* Company/Industry for clients */}
              {profile.role === 'client' && (
                <>
                  {profile.industry && (
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="truncate">{profile.industry}</span>
                    </div>
                  )}
                  {profile.projectCount !== undefined && profile.projectCount > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <Briefcase className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span>{profile.projectCount} project{profile.projectCount !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                </>
              )}

              {/* Service types for filmmakers */}
              {profile.role === 'filmmaker' && profile.serviceTypes && profile.serviceTypes.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {profile.serviceTypes.slice(0, 3).map((service, idx) => (
                    <Badge key={idx} variant="secondary" className="text-[10px] px-1.5 py-0">
                      {service}
                    </Badge>
                  ))}
                  {profile.serviceTypes.length > 3 && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      +{profile.serviceTypes.length - 3} more
                    </Badge>
                  )}
                </div>
              )}

              {/* Bio excerpt */}
              {profile.bio && (
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {profile.bio}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="p-3 border-t bg-muted/20 flex gap-2">
              {onViewFullProfile && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 h-8 text-xs"
                  onClick={() => {
                    setIsOpen(false);
                    onViewFullProfile();
                  }}
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  View Profile
                </Button>
              )}
              {onStartChat && (
                <Button
                  size="sm"
                  className="flex-1 h-8 text-xs"
                  onClick={() => {
                    setIsOpen(false);
                    onStartChat();
                  }}
                >
                  <MessageSquare className="h-3.5 w-3.5 mr-1" />
                  Message
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Failed to load profile
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
